import { db } from "@/firebaseConfig.js";
import dayjs from "dayjs";
import "react-quill-new/dist/quill.snow.css";
import { Table, Tag, Button, Popconfirm, message, Form } from "antd";
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  deleteDoc,
  doc,
} from "firebase/firestore";
import TransactionDrawer from "./PaymentDrawer";
import useAllUsers from "@/utils/fetchAllUsers.js";

const TransactionsTable = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = Form.useForm();

  const { users } = useAllUsers();

  const usersMap = useMemo(() => {
    return users.reduce((acc, u) => {
      acc[u.uid] = u;
      return acc;
    }, {});
  }, [users]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "paymentCalendar"),
        orderBy("createdAt", "desc"),
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setTransactions(data);
    } catch (error) {
      console.error("Ошибка при загрузке транзакций:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "paymentCalendar", id));
      message.success("Transaction successfully deleted");

      fetchTransactions();
    } catch (error) {
      console.error("Ошибка при удалении:", error);
      message.error("Failed to delete the transaction");
    }
  };

  const handleEdit = (record) => {
    setCurrentRecord(record);
    form.setFieldsValue({
      amount: record.amount,
      status: record.status,
      paymentDate: record.paymentDate
        ? dayjs(new Date(record.paymentDate.seconds * 1000))
        : null,
    });

    setDrawerVisible(true);
    openDrawerForEdit(record);
  };

  const columns = [
    { title: "User Email", dataIndex: "userEmail", key: "userEmail" },

    {
      title: "Account Details",
      key: "accountDetails",
      render: (_, record) => usersMap[record.userId]?.accountDetails || "-",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color;
        switch (status) {
          case "PENDING":
            color = "gold";
            break;
          case "APPROVED":
            color = "green";
            break;
          case "REJECT":
            color = "red";
            break;
          default:
            color = "default";
        }
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (date) =>
        date ? new Date(date.seconds * 1000).toLocaleDateString() : "-",
      sorter: (a, b) => a.paymentDate.seconds - b.paymentDate.seconds,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) =>
        date ? new Date(date.seconds * 1000).toLocaleString() : "-",
      sorter: (a, b) => a.createdAt.seconds - b.createdAt.seconds,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="primary" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete the transaction?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const [drawerMode, setDrawerMode] = useState("add");

  // открыть для добавления
  const openDrawerForAdd = () => {
    setDrawerMode("add");
    setCurrentRecord(null);
    setDrawerVisible(true);
  };

  // открыть для редактирования
  const openDrawerForEdit = (record) => {
    setDrawerMode("edit");
    setCurrentRecord(record);
    setDrawerVisible(true);
  };

  return (
    <>
      <Button onClick={openDrawerForAdd} type="primary">
        Add new transaction
      </Button>
      <Table columns={columns} dataSource={transactions} rowKey="id" />

      <TransactionDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        record={currentRecord}
        mode={drawerMode}
        users={usersMap}
        onSaved={fetchTransactions}
      />
    </>
  );
};

export default TransactionsTable;
